import {
  MICE_CORPORATE_PATHS,
  type MiceCorporatePath,
  type MiceNamedRef,
  type MiceRequesterOption,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import {
  Card,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToneChip,
} from '@web/ui/core';
import { SearchPicker } from './search_picker';
import type { MiceProjectFormState } from '../hooks/use_mice_project_form';

export function RefSelect(props: {
  id: string;
  label: string;
  items: readonly MiceNamedRef[];
  value: MiceNamedRef | null;
  onChange: (value: MiceNamedRef | null) => void;
  optional?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={props.id}>{props.label}</Label>
      <Select
        value={props.value?.id ?? ''}
        onValueChange={(id) => {
          props.onChange(props.items.find((i) => i.id === id) ?? null);
        }}
      >
        <SelectTrigger id={props.id} aria-label={props.label}>
          <SelectValue
            placeholder={props.optional ? t('mice.form.optional') : t('mice.form.choose')}
          />
        </SelectTrigger>
        <SelectContent position="item-aligned">
          {props.items.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {item.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Como o campo é definido: para o evento todo, por viajante na planilha, ou pelas regras VOLL. */
function PathSelect(props: {
  id: string;
  label: string;
  value: MiceCorporatePath;
  onChange: (value: MiceCorporatePath) => void;
  allowEvent: boolean;
}): React.JSX.Element {
  const paths = MICE_CORPORATE_PATHS.filter((p) => p !== 'event' || props.allowEvent);
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={props.id}>{props.label}</Label>
      <Select
        value={props.value}
        onValueChange={(value) => {
          props.onChange(value as MiceCorporatePath);
        }}
      >
        <SelectTrigger id={props.id} aria-label={props.label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent position="item-aligned">
          {paths.map((path) => (
            <SelectItem key={path} value={path}>
              {t(`mice.form.path.${path}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DateField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-1 flex-col gap-1">
      <Label htmlFor={props.id}>{props.label}</Label>
      <Input
        id={props.id}
        type="date"
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
      />
    </div>
  );
}

function SectionTitle({ children }: { children: string }): React.JSX.Element {
  return <h2 className="text-base font-semibold text-foreground">{children}</h2>;
}

const requesterLabel = (r: MiceRequesterOption): string =>
  r.name ? `${r.name} · ${r.email}` : r.email;

function RequesterOrigin({ requester }: { requester: MiceRequesterOption }): React.JSX.Element {
  return (
    <ToneChip tone={requester.origin === 'voll' ? 'brand' : 'gray'}>
      {t(`mice.form.requesterOrigin.${requester.origin}`)}
    </ToneChip>
  );
}

const requesterBadge = (r: MiceRequesterOption): React.JSX.Element => (
  <RequesterOrigin requester={r} />
);

/** Etapa 1: nome e período do evento. */
export function EventStep({ form }: Readonly<{ form: MiceProjectFormState }>): React.JSX.Element {
  return (
    <Card>
      <div className="flex flex-col gap-4 p-4">
        <SectionTitle>{t('mice.form.eventSection')}</SectionTitle>
        <div className="flex flex-col gap-1">
          <Label htmlFor="mice-name">{t('mice.form.name')}</Label>
          <Input
            id="mice-name"
            value={form.name}
            onChange={(e) => {
              form.setName(e.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <DateField
            id="mice-event-start"
            label={t('mice.form.eventStart')}
            value={form.eventStart}
            onChange={form.setEventStart}
          />
          <DateField
            id="mice-event-end"
            label={t('mice.form.eventEnd')}
            value={form.eventEnd}
            onChange={form.setEventEnd}
          />
        </div>
        {form.rangeInvalid ? (
          <p className="text-meta text-tone-red-ink">{t('mice.form.eventRangeInvalid')}</p>
        ) : null}
      </div>
    </Card>
  );
}

/** Etapa 2: a cascata corporativa (cliente → workspace → débito → solicitante). */
export function CorporateStep({
  form,
}: Readonly<{ form: MiceProjectFormState }>): React.JSX.Element {
  const workspaceData = form.workspaceOptions;
  const debitData = form.debitCompanyOptions;
  return (
    <Card>
      <div className="flex flex-col gap-4 p-4">
        <SectionTitle>{t('mice.form.corporateSection')}</SectionTitle>
        <SearchPicker
          id="mice-client"
          label={t('mice.form.client')}
          placeholder={t('mice.form.clientSearch')}
          value={form.client}
          onChange={form.setClient}
          {...form.clientSearch}
          keyOf={(c) => c.id}
          labelOf={(c) => c.name}
        />
        {form.client ? (
          <RefSelect
            id="mice-workspace"
            label={t('mice.form.workspace')}
            items={form.client.workspaces}
            value={form.workspace}
            onChange={form.setWorkspace}
          />
        ) : null}
        {workspaceData?.debitCompanies.length === 0 ? (
          <p className="text-meta text-tone-red-ink">{t('mice.form.noDebitCompany')}</p>
        ) : null}
        {workspaceData && workspaceData.debitCompanies.length > 0 ? (
          <RefSelect
            id="mice-debit-company"
            label={t('mice.form.debitCompany')}
            items={workspaceData.debitCompanies}
            value={form.debitCompany}
            onChange={form.setDebitCompany}
          />
        ) : null}
        {debitData ? (
          <>
            <PathSelect
              id="mice-cc-path"
              label={t('mice.form.costCenterPath')}
              value={form.costCenterPath}
              onChange={form.setCostCenterPath}
              allowEvent={form.costCenterEventAllowed}
            />
            {form.costCenterPath === 'event' ? (
              <RefSelect
                id="mice-cc"
                label={t('mice.form.costCenter')}
                items={debitData.costCenters}
                value={form.costCenter}
                onChange={form.setCostCenter}
              />
            ) : null}
            {debitData.projects.length > 0 ? (
              <RefSelect
                id="mice-project"
                label={t('mice.form.corporateProject')}
                items={debitData.projects}
                value={form.corporateProject}
                onChange={form.setCorporateProject}
                optional
              />
            ) : null}
          </>
        ) : null}
        {workspaceData ? (
          <>
            <PathSelect
              id="mice-motive-path"
              label={t('mice.form.motivePath')}
              value={form.motivePath}
              onChange={form.setMotivePath}
              allowEvent={form.motiveEventAllowed}
            />
            {form.motivePath === 'event' ? (
              <RefSelect
                id="mice-motive"
                label={t('mice.form.motive')}
                items={workspaceData.motives}
                value={form.motive}
                onChange={form.setMotive}
              />
            ) : null}
            <SearchPicker
              id="mice-requester"
              label={t('mice.form.requester')}
              placeholder={t('mice.form.requesterSearch')}
              value={form.requester}
              onChange={form.setRequester}
              {...form.requesterSearch}
              keyOf={(r) => `${r.origin}:${r.id}`}
              labelOf={requesterLabel}
              badgeOf={requesterBadge}
            />
          </>
        ) : null}
        {form.optionsFailed ? (
          <p className="text-meta text-tone-red-ink">{t('mice.form.loadFailed')}</p>
        ) : null}
      </div>
    </Card>
  );
}
